import { useCallback, useEffect, useRef } from "react";
import { useLatest } from "hooks/useLatest";
import { scrollIntoViewIfNeeded } from "utils/scrollIntoViewIfNeeded";
import type { UseAutoScrollToRefArgs, UseAutoScrollToRefResult } from "./types";

export const useAutoScrollToRef = <
  TContainer extends HTMLElement = HTMLElement,
  TTarget extends HTMLElement = HTMLElement,
>({
  resetDep,
  options,
}: UseAutoScrollToRefArgs): UseAutoScrollToRefResult<TContainer, TTarget> => {
  const containerRef = useRef<TContainer | null>(null);
  const targetRef = useRef<TTarget | null>(null);

  const optionsRef = useLatest(options);

  const scrollToTarget = useCallback(() => {
    const container = containerRef.current;
    const target = targetRef.current;
    if (!container || !target) return;

    scrollIntoViewIfNeeded({
      container,
      target,
      options: optionsRef.current,
    });
  }, [optionsRef]);

  useEffect(() => {
    requestAnimationFrame(scrollToTarget);
  }, [resetDep, scrollToTarget]);

  return { scrollToTarget, containerRef, targetRef };
};
